import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck, CheckCircle2, ListChecks, Clock, Palette } from "lucide-react";
import { sr } from "../../lib/sr";

const STATUS_BADGE = { planning: "sr-badge-gray", in_production: "sr-badge-amber", in_review: "sr-badge-amber", approved: "sr-badge-green" };

export default function ClientPortal() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [brandOpen, setBrandOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState("");
  const [brandColor, setBrandColor] = useState("#F2A33D");
  const [savingBrand, setSavingBrand] = useState(false);

  const load = () => sr.clientPortal().then(setData).catch(() => setData({ error: true }));
  useEffect(() => { load(); }, []);

  const openBrand = () => {
    setLogoUrl(data?.branding?.logo_url || "");
    setBrandColor(data?.branding?.brand_color || "#F2A33D");
    setBrandOpen((o) => !o);
  };
  const saveBrand = async () => {
    setSavingBrand(true);
    try { await sr.portalSetBranding({ logo_url: logoUrl, brand_color: brandColor }); toast.success("Portal branding updated"); setBrandOpen(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save branding"); }
    finally { setSavingBrand(false); }
  };

  const decide = async (aid, decision) => {
    setBusy(aid);
    try { await sr.decideApproval(aid, { decision, comment: "" }); toast.success(decision === "approve" ? "Approved" : "Sent back"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not record decision"); }
    finally { setBusy(null); }
  };

  if (!data) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;
  if (data.error) return <div className="sr-card"><p className="sr-muted">Could not load your portal.</p></div>;

  const { tenant, branding, approvals_pending, projects, counts } = data;
  const accent = branding?.brand_color || "#F2A33D";

  return (
    <div data-testid="client-portal-page">
      <div style={{ borderRadius: 12, background: "#fff", border: "1px solid var(--border)", borderTop: `4px solid ${accent}`, padding: 18, marginBottom: 18 }} data-testid="portal-brand-header">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            {branding?.logo_url
              ? <img src={branding.logo_url} alt="logo" data-testid="portal-logo" style={{ height: 40, maxWidth: 180, objectFit: "contain" }} />
              : <span className="sr-badge" style={{ background: accent, color: "#fff", border: "none" }}><ShieldCheck size={12} /> Client portal</span>}
            <div>
              <h1 style={{ fontSize: 26 }}>{tenant?.name || "Your workspace"}</h1>
              <p className="sr-muted" style={{ fontSize: 13 }}>Your campaigns and anything waiting on your sign-off.</p>
            </div>
          </div>
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={openBrand} data-testid="portal-customize-btn">
            <Palette size={14} /> Customize
          </button>
        </div>
        {brandOpen && (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginTop: 14, borderTop: "1px solid var(--border)", paddingTop: 14 }} data-testid="portal-brand-form">
            <div className="sr-field" style={{ margin: 0, flex: 1, minWidth: 240 }}>
              <label className="sr-label" style={{ fontSize: 12 }}>Logo URL</label>
              <input className="sr-input" placeholder="https://…/logo.png" value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} data-testid="portal-logo-input" />
            </div>
            <div className="sr-field" style={{ margin: 0 }}>
              <label className="sr-label" style={{ fontSize: 12 }}>Brand color</label>
              <input type="color" value={brandColor} onChange={(e) => setBrandColor(e.target.value)} data-testid="portal-color-input"
                style={{ width: 56, height: 40, border: "1px solid var(--border)", borderRadius: 8, background: "#fff", cursor: "pointer" }} />
            </div>
            <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={saveBrand} disabled={savingBrand} data-testid="portal-brand-save">
              {savingBrand ? <Loader2 size={14} className="sr-spin" /> : "Save branding"}
            </button>
          </div>
        )}
      </div>

      <div className="sr-card" style={{ marginBottom: 18 }} data-testid="portal-approvals-card">
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <CheckCircle2 size={16} style={{ color: "var(--blue)" }} />
          <strong style={{ color: "var(--navy)" }}>Waiting for your approval</strong>
          <span className="sr-badge sr-badge-amber" data-testid="portal-approvals-count">{counts.approvals_pending}</span>
        </div>
        {approvals_pending.length === 0 ? (
          <p className="sr-muted" style={{ fontSize: 13 }} data-testid="portal-approvals-empty">Nothing needs your sign-off right now.</p>
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            {approvals_pending.map((a) => (
              <div key={a.id} className="sr-card" data-testid={`portal-approval-${a.id}`} style={{ background: "#fff" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 15, color: "var(--navy)", fontWeight: 700, textTransform: "capitalize" }}>{a.campaign_name}</div>
                    <div className="sr-muted" style={{ fontSize: 12 }}>Version {a.version} · submitted by {a.submitted_by_email} · waiting {Math.round(a.waiting_hours || 0)}h</div>
                    {a.summary ? <p className="sr-muted" style={{ fontSize: 13, marginTop: 4 }}>{a.summary}</p> : null}
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <Link to={`/app/campaigns/${a.campaign_id}/project`} className="sr-btn sr-btn-ghost sr-btn-sm" data-testid={`portal-open-${a.id}`}>Review</Link>
                    <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => decide(a.id, "reject")} disabled={busy === a.id} data-testid={`portal-reject-${a.id}`}>Send back</button>
                    <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => decide(a.id, "approve")} disabled={busy === a.id} data-testid={`portal-approve-${a.id}`}>
                      {busy === a.id ? <Loader2 size={14} className="sr-spin" /> : <><CheckCircle2 size={14} /> Approve</>}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="sr-card" data-testid="portal-projects-card">
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <ListChecks size={16} style={{ color: "var(--blue)" }} />
          <strong style={{ color: "var(--navy)" }}>Your campaigns</strong>
          <span className="sr-badge sr-badge-gray">{counts.projects}</span>
        </div>
        {projects.length === 0 ? (
          <p className="sr-muted" style={{ fontSize: 13 }}>No campaign projects yet.</p>
        ) : (
          <div style={{ display: "grid", gap: 8 }}>
            {projects.map((p) => {
              const pct = p.progress.total ? Math.round((p.progress.done / p.progress.total) * 100) : 0;
              return (
                <div key={p.campaign_id} className="sr-card" data-testid={`portal-project-${p.campaign_id}`} style={{ background: "#fff" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <strong style={{ color: "var(--navy)", textTransform: "capitalize" }}>{p.name}</strong>
                      <span className="sr-badge sr-badge-gray" style={{ textTransform: "capitalize" }}>{p.campaign_type}</span>
                      <span className={`sr-badge ${STATUS_BADGE[p.project_status] || "sr-badge-gray"}`} style={{ textTransform: "capitalize" }}>{(p.project_status || "").replace("_", " ")}</span>
                    </div>
                    <Link to={`/app/campaigns/${p.campaign_id}/project`} className="sr-btn sr-btn-ghost sr-btn-sm" data-testid={`portal-project-open-${p.campaign_id}`}>Open</Link>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
                    <div style={{ flex: 1, height: 8, background: "var(--blue-light)", borderRadius: 999, overflow: "hidden" }}>
                      <div style={{ width: `${pct}%`, height: "100%", background: "var(--blue)" }} />
                    </div>
                    <span className="sr-muted" style={{ fontSize: 12 }}><Clock size={11} /> {p.progress.done}/{p.progress.total}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
